import React from 'react';
import { z } from 'zod';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// About info — faithful rewrite of legacy molecules/AboutInfo (Strapi
// sections.about-new-info). Two-column heading + description, with a decorative
// icon/blob overlapping behind the heading on desktop (e.g. the green shape
// behind "Identity").
export const aboutInfoSchema = z.object({
  heading: z.string().min(1),
  description: z.string().optional(),
  iconUrl: z.string().optional(),
});

type AboutInfoProps = z.infer<typeof aboutInfoSchema>;

const Section = styled.section`
  position: relative;
  max-width: 112rem;
  margin: 0 auto;
  padding: 3rem 1.6rem;

  @media (min-width: 48em) {
    padding: 10rem 1.6rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  column-gap: 6rem;
  row-gap: 1.6rem;

  @media (min-width: 48em) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Blob = styled.img`
  display: none;

  @media (min-width: 48em) {
    display: block;
    position: absolute;
    left: 34%;
    top: -0.6rem;
    width: 16.8rem;
    height: 11.2rem;
    object-fit: contain;
    z-index: 0;
  }
`;

const Heading = styled.h2`
  position: relative;
  z-index: 1;
  margin: 0;
  color: #1a1b1f;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 2.4rem;
  letter-spacing: -2px;

  @media (min-width: 48em) {
    font-size: 5.6rem;
    line-height: 7.1rem;
  }
`;

const Description = styled.p`
  margin: 0;
  color: #1a1b1f;
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 1.6rem;
  line-height: 1.6;

  @media (min-width: 48em) {
    font-size: 1.8rem;
  }
`;

function AboutInfo({ heading, description, iconUrl }: AboutInfoProps) {
  return (
    <Section>
      {iconUrl ? <Blob src={iconUrl} alt="" /> : null}
      <Grid>
        <Heading>{heading}</Heading>
        {description ? <Description>{description}</Description> : null}
      </Grid>
    </Section>
  );
}

export const aboutInfoBlock: BlockDefinition<AboutInfoProps> = {
  type: 'aboutInfo',
  schema: aboutInfoSchema,
  Renderer: AboutInfo,
};
